/**
 * Reviio — Google Analytics Event Tracking
 * 
 * Centralised helper for sending GA4 events via gtag().
 * Usage:  import { trackEvent } from '../services/analytics';
 *         trackEvent('banner_click', { label: 'open_dashboard' });
 */

// Extend the Window interface so TypeScript knows about gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * GA4 Measurement ID — used in send_to to ensure events route
 * directly to GA4 even when GTM is also loaded on the page.
 */
const GA4_ID = 'G-N2Z6EKPGRT';

/**
 * Fire a GA4 custom event.
 * Silently no-ops if gtag isn't loaded (e.g. ad-blockers, dev mode).
 * Uses send_to to bypass GTM delegation.
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, { ...params, send_to: GA4_ID });
  }
}

/**
 * Send a GA4 page_view for SPA soft navigation.
 * Since send_page_view is disabled in the global config, we fire
 * manual page_view events with full page_location so GA4 correctly
 * attributes each view to the right page.
 */
export function trackPageView(path: string, title?: string) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_title: title || document.title,
      page_location: window.location.origin + path,
      page_path: path,
      send_to: GA4_ID,
    });
  }
}

// ──────────────────────────────────────────────
// Pre-defined event helpers for consistency
// ──────────────────────────────────────────────

/** Demo banner was shown to the user */
export const trackBannerView = () =>
  trackEvent('demo_banner_view', { component: 'demo_banner' });

/** User clicked the CTA button inside the banner */
export const trackBannerClick = () =>
  trackEvent('demo_banner_click', { component: 'demo_banner', action: 'open_dashboard' });

/** User dismissed the banner with the X button */
export const trackBannerDismiss = () =>
  trackEvent('demo_banner_dismiss', { component: 'demo_banner', action: 'close' });

/** User copied demo credentials */
export const trackBannerCopy = (field: string) =>
  trackEvent('demo_banner_copy', { component: 'demo_banner', action: 'copy', field });

/** CTA buttons on the landing page */
export const trackLandingCTA = (label: string) =>
  trackEvent('landing_cta_click', { component: 'landing_page', label });

/** Pricing card CTA click */
export const trackPricingClick = (plan: string) =>
  trackEvent('pricing_click', { component: 'pricing', plan });

/** Walkthrough page view */
export const trackWalkthroughView = (slug: string) =>
  trackEvent('walkthrough_view', { component: 'walkthrough', slug });

/** Feature page view */
export const trackFeaturePageView = (slug: string) =>
  trackEvent('feature_page_view', { component: 'feature_page', slug });

/** Contact form submission */
export const trackContactSubmit = () =>
  trackEvent('contact_form_submit', { component: 'landing_page' });

/** Navbar link click */
export const trackNavClick = (label: string) =>
  trackEvent('nav_click', { component: 'navbar', label });

/** Footer link click */
export const trackFooterClick = (label: string) =>
  trackEvent('footer_click', { component: 'footer', label });

/** Blog card click */
export const trackBlogClick = (slug: string) =>
  trackEvent('blog_card_click', { component: 'landing_page', slug });

/** Signup click */
export const trackSignupClick = (source: string) =>
  trackEvent('signup_click', { source });

// ──────────────────────────────────────────────
// Dashboard events
// ──────────────────────────────────────────────

/** Dashboard page navigation (fires on every route change inside /dashboard) */
export const trackDashboardPageView = (page: string) =>
  trackEvent('dashboard_page_view', { component: 'dashboard', page });

/** Dashboard sidebar nav click */
export const trackDashboardNavClick = (label: string) =>
  trackEvent('dashboard_nav_click', { component: 'sidebar', label });

/** Review action (publish, regenerate, edit, etc.) */
export const trackReviewAction = (action: string, reviewId?: string) =>
  trackEvent('review_action', { component: 'reviews', action, ...(reviewId ? { review_id: reviewId } : {}) });

/** Location action (add, edit, delete, connect) */
export const trackLocationAction = (action: string) =>
  trackEvent('location_action', { component: 'locations', action });

/** Campaign action (create, send, view) */
export const trackCampaignAction = (action: string) =>
  trackEvent('campaign_action', { component: 'campaigns', action });

/** Settings change */
export const trackSettingsChange = (section: string) =>
  trackEvent('settings_change', { component: 'settings', section });

/** Billing / checkout action */
export const trackBillingAction = (action: string, plan?: string) =>
  trackEvent('billing_action', { component: 'billing', action, ...(plan ? { plan } : {}) });

/** User logout */
export const trackLogout = () =>
  trackEvent('logout', { component: 'dashboard' });
